import { Loader2, CheckCircle2, AlertCircle, Clock } from "lucide-react";

interface StatusPillProps {
  status?: string;
  stage?: string;
  message?: string;
}

export function StatusPill({ status, stage, message }: StatusPillProps) {
  const statusMap = {
    uploaded: 'bg-muted text-muted-foreground',
    processing: 'bg-primary/10 text-primary',
    completed: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    error: 'bg-destructive/10 text-destructive',
    backoff_wait: 'bg-orange-500/10 text-orange-700 border-orange-200 dark:text-orange-300 dark:border-orange-800',
    failed: 'bg-destructive/10 text-destructive',
  } as const;

  const displayStatus = status as keyof typeof statusMap;
  const className = statusMap[displayStatus] || statusMap.uploaded;

  const getDisplayText = () => {
    if (status === 'error') return 'Error';
    if (status === 'completed') return 'Completed';
    if (stage === 'busy') return 'Already processing';
    if (stage === 'rate_limited') return 'Rate limited';
    if (status === 'backoff_wait' || stage === 'backoff_wait') return 'Waiting to retry';
    if (stage) {
      const stageMap: Record<string, string> = {
        queued: 'Queued',
        downloading: 'Downloading',
        pdf_to_png: 'Converting PDF',
        ai_extract: 'AI Processing',
        normalizing: 'Processing Data',
        saving: 'Saving Items',
        completed: 'Completed',
        busy: 'Already processing',
        rate_limited: 'Rate limited',
        backoff_wait: 'Waiting to retry'
      };
      return stageMap[stage] || stage;
    }
    return 'Uploaded';
  };

  const getIcon = () => {
    if (status === 'processing' || stage === 'busy' || stage === 'rate_limited') return <Loader2 className="h-3 w-3 animate-spin" />;
    if (status === 'backoff_wait' || stage === 'backoff_wait') return <Clock className="h-3 w-3" />;
    if (status === 'completed') return <CheckCircle2 className="h-3 w-3" />;
    if (status === 'error') return <AlertCircle className="h-3 w-3" />;
    return null;
  };

  return (
    <span 
      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium ${className}`}
      title={message || ''}
    >
      {getIcon()}
      {getDisplayText()}
    </span>
  );
}